import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, ActionIcon, ScrollArea, Stack, Badge, Group,
  Textarea, Avatar, Divider, Tooltip, Modal, Button, Paper
} from '@mantine/core';
import {
  IconMicrophone, IconPlayerStop, IconSend, IconLogin, IconRobot,
  IconCheck, IconX, IconPlus, IconChevronDown, IconClock, IconMessage, IconTrash,
  IconCamera, IconFold
} from '@tabler/icons-react';
import {
  collection, addDoc, onSnapshot, doc, updateDoc, serverTimestamp,
  query, orderBy, limit, where, getDocs, deleteDoc
} from 'firebase/firestore';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import useSpeechRecognition from './hooks/useSpeechRecognition';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Component } from 'react';

// ─── SafeMarkdown Error Boundary ──────────────────────────────────────────────
class SafeMarkdown extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('SafeMarkdown caught rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          p="xs"
          style={{
            background: 'rgba(250, 82, 82, 0.08)',
            border: '1px solid rgba(250, 82, 82, 0.3)',
            borderRadius: 8,
            overflow: 'auto',
          }}
        >
          <Text size="xs" c="red.4" fw={700} mb={4}>⚠️ Failed to render formatted Markdown:</Text>
          <Text size="xs" c="red.2" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', lineHeight: 1.4 }} mb={8}>
            {this.state.error?.message || String(this.state.error)}
          </Text>
          <Divider my={6} color="rgba(250,82,82,0.15)" />
          <Text size="11px" c="dimmed" mb={2} fw={600}>Raw text content fallback:</Text>
          <pre style={{ fontSize: '11px', color: '#e9ecef', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.5 }}>
            {this.props.rawText}
          </pre>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ─── Log Line ────────────────────────────────────────────────────────────────
function classifyLog(log) {
  if (log.startsWith('🔧')) return 'action';
  if (log.startsWith('📋')) return 'result';
  if (log.startsWith('💬')) return 'thought';
  if (log.startsWith('✅') || log.startsWith('[SUCCESS]')) return 'success';
  if (log.startsWith('❌') || log.startsWith('[ERROR]')) return 'error';
  if (log.startsWith('⛔')) return 'abort';
  if (log.startsWith('🤖')) return 'system';
  return 'info';
}

function LogLine({ log }) {
  const type = classifyLog(log);
  const colors = { action: '#339af0', result: '#868e96', thought: '#b197fc', success: '#51cf66', error: '#fa5252', abort: '#fd7e14', system: '#845ef7', info: '#ced4da' };
  
  if (type === 'thought') {
    const textOnly = log.replace(/^💬\s*/, '');
    return (
      <Flex gap={6} align="flex-start" py={2} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
        <Text size="11px" style={{ color: colors.thought, lineHeight: 1.4, userSelect: 'none' }}>💬</Text>
        <Box flex={1} className="markdown-body" style={{ fontSize: '11px', lineHeight: 1.4, color: '#e9ecef' }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {textOnly}
          </ReactMarkdown>
        </Box>
      </Flex>
    );
  }

  return <Text style={{ fontSize: '0.68rem', lineHeight: 1.4, color: colors[type], fontFamily: 'monospace' }}>{log}</Text>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function relativeTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function sessionTitle(session, index, total) {
  if (!session) return 'Session';
  const num = total - index;
  const date = session.startedAt?.toDate
    ? session.startedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  return `Session ${num}${date ? ` · ${date}` : ''}`;
}

// ─── Session Picker Modal ─────────────────────────────────────────────────────
function SessionPicker({ opened, onClose, sessions, currentSessionId, onSelect, onNewSession, isCreating }) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="sm" style={{ letterSpacing: '2px', color: '#845ef7' }}>SESSIONS</Text>}
      size="sm"
      styles={{
        content: { background: '#111116', border: '1px solid rgba(255,255,255,0.08)' },
        header: { background: '#111116', borderBottom: '1px solid rgba(255,255,255,0.06)' },
        title: { color: 'white' },
        close: { color: '#868e96' },
      }}
    >
      <Stack gap={4} mt={4}>
        <Button
          leftSection={<IconPlus size={14} />}
          variant="light"
          color="violet"
          size="xs"
          fullWidth
          onClick={onNewSession}
          loading={isCreating}
        >
          New Session
        </Button>

        <Divider my={4} color="rgba(255,255,255,0.06)" />

        {sessions.length === 0 && (
          <Text size="xs" c="dimmed" ta="center" py={8}>No past sessions yet.</Text>
        )}

        {sessions.map((session, i) => {
          const isActive = session.id === currentSessionId;
          const title = sessionTitle(session, i, sessions.length);

          const handleDelete = async (e) => {
            e.stopPropagation();
            if (isActive) return;
            if (window.confirm('Delete this session and all its history?')) {
              try {
                await deleteDoc(doc(db, 'commanderSessions', session.id));
                const q = query(collection(db, 'commandQueue'), where('sessionId', '==', session.id));
                const snap = await getDocs(q);
                await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
              } catch (err) {
                console.error('Delete failed:', err);
              }
            }
          };

          return (
            <Paper
              key={session.id}
              p={8}
              style={{
                background: isActive ? 'rgba(132, 94, 247, 0.12)' : 'rgba(255,255,255,0.03)',
                border: isActive ? '1px solid rgba(132,94,247,0.3)' : '1px solid rgba(255,255,255,0.05)',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              onClick={() => { onSelect(session.id); onClose(); }}
            >
              <Group justify="space-between" mb={2}>
                <Group gap={6}>
                  <Text size="xs" fw={600} c={isActive ? 'violet.3' : 'gray.3'}>{title}</Text>
                  {isActive && <Badge size="xs" variant="dot" color="green">Active</Badge>}
                  {session.status === 'completed' && <Badge size="xs" variant="outline" color="gray">Ended</Badge>}
                </Group>
                <Group gap={6}>
                  <Text size="xs" c="dimmed">{relativeTime(session.startedAt)}</Text>
                  {!isActive && (
                    <ActionIcon size="xs" variant="subtle" color="red.8" onClick={handleDelete}>
                      <IconTrash size={10} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
              {session.summary ? (
                <Text size="xs" c="dimmed" lineClamp={2} style={{ lineHeight: 1.4 }}>
                  {session.summary}
                </Text>
              ) : (
                <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                  {session.commandCount ? `${session.commandCount} commands` : 'No commands yet'}
                </Text>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Modal>
  );
}

function compressImage(file, maxWidth = 1024, maxHeight = 1024, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function App() {
  const [commandText, setCommandText] = useState('');
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [user, setUser] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null);
  const fileInputRef = useRef(null);
  const prevLiveSessionIdRef = useRef(null);


  // Session state
  const [currentSession, setCurrentSession] = useState(null);  // bridge's live session state
  const [viewingSessionId, setViewingSessionId] = useState(null); // which session are we looking at?
  const [allSessions, setAllSessions] = useState([]);          // session list for picker
  const [viewingHistory, setViewingHistory] = useState([]);     // tasks for that session
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isBridgeOnline, setIsBridgeOnline] = useState(false);

  // UI state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showAllHistory, setShowAllHistory] = useState(false);
  const chatEndRef = useRef(null);
  const logsEndRef = useRef(null);

  const toggleTaskExpanded = (tid) => {
    setExpandedTasks(prev => ({
      ...prev,
      [tid]: prev[tid] === undefined ? false : !prev[tid]
    }));
  };

  // Auth
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  // Bridge status
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'commanderState', 'bridgeStatus'), (snap) => {
      const data = snap.data();
      if (!data) return;
      const lastSeen = data.lastSeen?.toDate ? data.lastSeen.toDate() : new Date(data.lastSeen);
      const isOnline = (Date.now() - lastSeen.getTime()) < 15000;
      setIsBridgeOnline(isOnline);
    });
    return () => unsub();
  }, []);

  // Current session tracker
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'commanderState', 'currentSession'), (snap) => {
      setCurrentSession(snap.data());
    });
    return () => unsub();
  }, []);

  // Auto-switch to live session when it changes
  useEffect(() => {
    if (currentSession?.sessionId) {
      if (currentSession.sessionId !== prevLiveSessionIdRef.current) {
        setViewingSessionId(currentSession.sessionId);
      }
      prevLiveSessionIdRef.current = currentSession.sessionId;
    }
  }, [currentSession?.sessionId, viewingSessionId]);

  // Session picker list
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'commanderSessions'), orderBy('startedAt', 'desc'), limit(20));
    const unsub = onSnapshot(q, (snap) => {
      setAllSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('❌ commanderSessions Snapshot Error:', err);
    });
    return () => unsub();
  }, [user]);

  // Viewing history (tasks) for the selected session
  useEffect(() => {
    if (!user || !viewingSessionId) return;
    const q = query(
      collection(db, 'commandQueue'),
      where('sessionId', '==', viewingSessionId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Sort in memory to bypass composite index requirements
      tasks.sort((a, b) => {
        const getMs = (val) => {
          if (!val) return Date.now();
          if (val.toDate) return val.toDate().getTime();
          const ms = new Date(val).getTime();
          return isNaN(ms) ? Date.now() : ms;
        };
        return getMs(a.createdAt) - getMs(b.createdAt);
      });

      setViewingHistory(tasks);
      // Auto-focus the latest active task, including quick completions from the PC portal
      const running = tasks.find(t => t.status === 'running');
      if (running) {
        setActiveTaskId(running.id);
      } else if (tasks.length > 0) {
        setActiveTaskId(tasks[tasks.length - 1].id);
      }
    }, (err) => {
      console.error('❌ Command Queue Snapshot Error:', err);
    });
    return () => unsub();
  }, [user, viewingSessionId]);

  // Keep activeTask object in sync with viewingHistory
  useEffect(() => {
    if (activeTaskId) {
      const t = viewingHistory.find(x => x.id === activeTaskId);
      setActiveTask(t || null);
    } else {
      setActiveTask(null);
    }
  }, [activeTaskId, viewingHistory]);

  // Auto-scroll logic
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [viewingHistory.length]);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTask?.logs?.length]);

  // Speech
  const { isListening, transcription, startListening, stopListening } = useSpeechRecognition();
  useEffect(() => {
    if (transcription) setCommandText(transcription);
  }, [transcription]);

  const handleLogin = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider);
  };

  const handleNewSession = async () => {
    setIsCreatingSession(true);
    try {
      await updateDoc(doc(db, 'commanderState', 'currentSession'), {
        requestNewSession: true
      });
      // Rotation logic happens in bridge.js (local computer)
      // It will update commanderState once finished.
      setTimeout(() => setIsCreatingSession(false), 2000);
    } catch (e) {
      console.error(e);
      setIsCreatingSession(false);
    }
  };

  const handleSend = async () => {
    if (!commandText.trim() && !attachedImage) return;

    const payload = {
      command: commandText.trim(),
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      logs: [],
      userId: user?.uid || 'anonymous',
      sessionId: currentSession?.sessionId || 'unknown',
      image: attachedImage
    };

    setCommandText('');
    setAttachedImage(null);
    const docRef = await addDoc(collection(db, 'commandQueue'), payload);
    setActiveTaskId(docRef.id);
  };

  const handleAbort = async () => {
    if (!activeTaskId) return;
    await updateDoc(doc(db, 'commandQueue', activeTaskId), {
      status: 'aborting'
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await compressImage(file);
      setAttachedImage(base64);
    } catch (err) {
      console.error('Image processing failed:', err);
    }
  };

  if (!user) {
    return (
      <Flex h="100vh" bg="#0a0a0c" align="center" justify="center">
        <Stack align="center" gap="xl">
          <IconRobot size={64} color="#845ef7" stroke={1.5} />
          <Stack align="center" gap={4}>
            <Text fw={900} size="xl" style={{ letterSpacing: '2px', color: 'white' }}>COMMANDER</Text>
            <Text c="dimmed" size="sm">AI Orchestration Portal</Text>
          </Stack>
          <Button
            leftSection={<IconLogin size={18} />}
            variant="gradient"
            gradient={{ from: 'violet', to: 'indigo' }}
            size="md"
            radius="md"
            onClick={handleLogin}
          >
            Connect via Google
          </Button>
        </Stack>
      </Flex>
    );
  }

  const isLive = viewingSessionId === currentSession?.sessionId;

  // Collapsible past history calculations
  const collapsedCount = viewingHistory.length > 3 ? viewingHistory.length - 3 : 0;
  const displayedTasks = (viewingHistory.length > 3 && !showAllHistory)
    ? viewingHistory.slice(viewingHistory.length - 3)
    : viewingHistory;

  return (
    <Box h="100vh" bg="#0a0a0c" style={{ overflow: 'hidden' }}>
      <SessionPicker
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        sessions={allSessions}
        currentSessionId={currentSession?.sessionId}
        isCreating={isCreatingSession}
        onSelect={setViewingSessionId}
        onNewSession={handleNewSession}
      />

      <Flex h="100%" direction="column">
        {/* Header */}
        <Flex p="md" align="center" justify="space-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Group gap="xs" onClick={() => setPickerOpen(true)} style={{ cursor: 'pointer' }}>
            <Avatar src={user.photoURL} size="sm" radius="xl" />
            <Stack gap={0}>
              <Group gap={6}>
                <Text size="xs" fw={700} style={{ letterSpacing: '1px', color: 'white' }}>
                  {isLive ? 'LIVE BRIDGE' : 'PAST SESSION'}
                </Text>
                <IconChevronDown size={12} color="#868e96" />
              </Group>
              <Group gap={4}>
                <Box style={{ width: 6, height: 6, borderRadius: '50%', background: isBridgeOnline ? '#51cf66' : '#fa5252' }} />
                <Text size="10px" c="dimmed">{isBridgeOnline ? 'Connected' : 'Offline'}</Text>
              </Group>
            </Stack>
          </Group>

          <Group gap="xs">
            <Tooltip label="Attach Image from Camera">
              <ActionIcon
                variant={attachedImage ? "filled" : "subtle"}
                color={attachedImage ? "violet" : "gray"}
                size="md"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach Image from Camera"
              >
                <IconCamera size={18} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Collapse all older responses">
              <ActionIcon
                variant="subtle"
                color="violet"
                size="md"
                onClick={() => setExpandedTasks({})}
                disabled={viewingHistory.length <= 1}
                aria-label="Collapse all older responses"
              >
                <IconFold size={18} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Active Task Details">
              <ActionIcon
                variant={activeTask ? 'light' : 'subtle'}
                color="violet"
                onClick={() => activeTask ? setActiveTaskId(null) : null}
                disabled={!activeTask}
                aria-label="Active Task Details"
              >
                <IconRobot size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Flex>

        {/* Timeline / History */}
        <Flex flex={1} style={{ overflow: 'hidden', position: 'relative' }}>
          <ScrollArea flex={1} p="md" scrollHideDelay={500}>
            <Stack gap={20} pb={20}>
              {/* Collapse indicator */}
              {collapsedCount > 0 && (
                <Box
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 14px',
                    background: 'rgba(132, 94, 247, 0.05)',
                    border: '1px dashed rgba(132,94,247,0.25)',
                    borderRadius: 10,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <IconChevronDown size={15} color="#845ef7" style={{ transform: showAllHistory ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  <Text size="xs" c="violet.4" fw={600} style={{ flex: 1 }}>
                    {showAllHistory ? 'Hide older commands' : `Show ${collapsedCount} older command${collapsedCount === 1 ? '' : 's'}`}
                  </Text>
                </Box>
              )}

              {displayedTasks.map((task) => {
                const isLastTask = task.id === viewingHistory[viewingHistory.length - 1]?.id;
                const isExpanded = expandedTasks[task.id] !== undefined
                  ? expandedTasks[task.id]
                  : (isLastTask || task.status === 'running');
                
                // Agents has content if it has a response, logs, or is actively working (running/pending)
                const hasAgentContent = task.response || ['pending', 'running'].includes(task.status) || task.status === 'error' || task.logs?.length > 0;

                return (
                  <Stack key={task.id} gap={8} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: 16 }}>

                    {/* USER BUBBLE */}
                    <Flex justify="flex-end">
                      <Box style={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                        borderRadius: '18px 18px 4px 18px',
                        maxWidth: '88%',
                        padding: '10px 14px',
                        boxShadow: '0 3px 16px rgba(109,40,217,0.45)',
                      }}>
                        {task.image && (
                          <Box style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 8, border: '1px solid rgba(255,255,255,0.15)' }}>
                            <img src={task.image} style={{ width: '100%', display: 'block', maxHeight: 180, objectFit: 'cover' }} />
                          </Box>
                        )}
                        <Text size="sm" c="white" fw={600} style={{ lineHeight: 1.5 }}>
                          {task.command || '(image only)'}
                        </Text>
                        <Text size="10px" style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'right', marginTop: 4 }}>
                          {relativeTime(task.createdAt)}
                        </Text>
                      </Box>
                    </Flex>

                    {/* AGENT SECTION */}
                    {hasAgentContent && (
                      <Flex justify="flex-start" align="flex-start" gap={8}>
                        {/* Avatar */}
                        <Box style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: 'rgba(132,94,247,0.2)',
                          border: '1px solid rgba(132,94,247,0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: 2,
                        }}>
                          <IconRobot size={14} color="#c084fc" />
                        </Box>

                        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>

                          {/* Running or Pending status */}
                          {(task.status === 'running' || task.status === 'pending') && (
                            <Group gap={8} style={{
                              background: 'rgba(109,40,217,0.12)',
                              border: '1px solid rgba(109,40,217,0.3)',
                              borderRadius: 8,
                              padding: '7px 12px',
                            }} justify="space-between">
                              <Group gap={8}>
                                <Box className="pulse-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: '#a78bfa', flexShrink: 0 }} />
                                <Text size="xs" style={{ color: '#c4b5fd', fontStyle: 'italic', fontWeight: 600 }}>
                                  {task.status === 'running' ? 'Processing...' : 'Thinking...'}
                                </Text>
                              </Group>
                              <Button size="compact-xs" variant="filled" color="red" radius="xl"
                                leftSection={<IconPlayerStop size={11} />} onClick={handleAbort}>
                                Stop
                              </Button>
                            </Group>
                          )}

                          {/* Error status */}
                          {task.status === 'error' && (
                            <Group gap={6} style={{
                              background: 'rgba(220,38,38,0.1)',
                              border: '1px solid rgba(220,38,38,0.3)',
                              borderRadius: 8,
                              padding: '7px 12px',
                            }}>
                              <IconX size={14} color="#f87171" />
                              <Text size="xs" style={{ color: '#f87171', fontWeight: 600 }}>Task Failed</Text>
                            </Group>
                          )}

                          {/* Collapse toggle bar */}
                          <Box
                            onClick={() => toggleTaskExpanded(task.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '10px 14px',
                              background: isExpanded ? 'rgba(109,40,217,0.18)' : 'rgba(109,40,217,0.08)',
                              border: `1px solid ${isExpanded ? 'rgba(139,92,246,0.45)' : 'rgba(139,92,246,0.2)'}`,
                              borderRadius: isExpanded ? '10px 10px 0 0' : '10px',
                              cursor: 'pointer',
                              userSelect: 'none',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <IconRobot size={13} color="#a78bfa" style={{ flexShrink: 0 }} />
                            <Text size="xs" fw={800} style={{ flex: 1, letterSpacing: '0.07em', color: '#c4b5fd' }}>
                              AGENT RESPONSE
                            </Text>
                            <IconChevronDown
                              size={17}
                              color="#a78bfa"
                              style={{
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                flexShrink: 0,
                              }}
                            />
                          </Box>

                          {/* Expanded content */}
                          {isExpanded && (task.response || task.logs?.length > 0 || ['pending', 'running'].includes(task.status)) && (
                            <Box style={{
                              background: '#0f0f1a',
                              borderRadius: '0 0 10px 10px',
                              border: '1px solid rgba(139,92,246,0.25)',
                              borderTop: 'none',
                              padding: '12px 14px',
                            }}>
                              {task.response && (
                                <Box className="cmd-md">
                                  <style>{`
                                    .cmd-md { color: #e2e8f0; font-size: 0.875rem; line-height: 1.7; }
                                    .cmd-md p { margin: 0 0 0.65rem; color: #e2e8f0; }
                                    .cmd-md p:last-child { margin-bottom: 0; }
                                    .cmd-md strong { color: #c084fc; font-weight: 700; }
                                    .cmd-md em { color: #a78bfa; }
                                    .cmd-md h1,.cmd-md h2,.cmd-md h3 {
                                      color: #c4b5fd; font-size: 0.92rem; font-weight: 700;
                                      margin: 0.9rem 0 0.35rem;
                                      padding-bottom: 4px;
                                      border-bottom: 1px solid rgba(139,92,246,0.25);
                                    }
                                    .cmd-md h1:first-child,.cmd-md h2:first-child,.cmd-md h3:first-child { margin-top: 0; }
                                    .cmd-md code {
                                      background: rgba(139,92,246,0.18); color: #c084fc;
                                      padding: 2px 6px; border-radius: 4px;
                                      font-family: 'JetBrains Mono','Fira Code',Consolas,monospace;
                                      font-size: 0.78rem; border: 1px solid rgba(139,92,246,0.3);
                                    }
                                    .cmd-md pre {
                                      background: #080810; padding: 11px 13px;
                                      border-radius: 8px; overflow-x: auto;
                                      border: 1px solid rgba(139,92,246,0.25); margin: 0.7rem 0;
                                    }
                                    .cmd-md pre code { background: none; padding: 0; border: none; color: #93c5fd; font-size: 0.8rem; }
                                    .cmd-md ul,.cmd-md ol { padding-left: 1.2rem; margin-bottom: 0.65rem; }
                                    .cmd-md li { margin-bottom: 0.25rem; color: #e2e8f0; }
                                    .cmd-md li::marker { color: #8b5cf6; }
                                    .cmd-md blockquote { border-left: 3px solid #7c3aed; padding-left: 12px; margin: 0.5rem 0; color: #a78bfa; font-style: italic; }
                                    .cmd-md a { color: #818cf8; }
                                    .cmd-md hr { border: none; border-top: 1px solid rgba(139,92,246,0.2); margin: 0.7rem 0; }
                                    .cmd-md table { width: 100%; border-collapse: collapse; margin-bottom: 0.7rem; font-size: 0.8rem; }
                                    .cmd-md th { background: rgba(109,40,217,0.25); color: #c4b5fd; font-weight: 700; padding: 5px 9px; border: 1px solid rgba(139,92,246,0.25); text-align: left; }
                                    .cmd-md td { padding: 4px 9px; border: 1px solid rgba(255,255,255,0.08); color: #e2e8f0; }
                                    .cmd-md tr:nth-child(even) td { background: rgba(255,255,255,0.025); }
                                  `}</style>
                                  <SafeMarkdown rawText={task.response}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.response}</ReactMarkdown>
                                  </SafeMarkdown>
                                </Box>
                              )}

                              {!task.response && ['pending', 'running'].includes(task.status) && (
                                <Group gap={6} p="xs">
                                  <div className="typing-dot" />
                                  <div className="typing-dot" />
                                  <div className="typing-dot" />
                                </Group>
                              )}

                              {/* Logs */}
                              {(task.id === activeTaskId || (task.status !== 'completed' && task.logs?.length > 0)) && (
                                <Box style={{
                                  background: 'rgba(0,0,0,0.4)',
                                  borderRadius: 8,
                                  padding: '8px 10px',
                                  marginTop: (task.response || (['pending', 'running'].includes(task.status) && !task.response)) ? 10 : 0,
                                  border: '1px solid rgba(255,255,255,0.04)',
                                }}>
                                  <Text size="10px" c="dimmed" fw={700} mb={4} style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                                    Logs
                                  </Text>
                                  <Stack gap={2}>
                                    {task.logs?.map((log, i) => <LogLine key={i} log={log} />)}
                                    <div ref={logsEndRef} />
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          )}

                        </Stack>
                      </Flex>
                    )}

                  </Stack>
                );
              })}
              <div ref={chatEndRef} />
            </Stack>
          </ScrollArea>
        </Flex>

        {/* Input Area */}
        <Box p="md" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: '#0a0a0c' }}>
          <Stack gap="xs">
            {attachedImage && (
              <Group p={4} style={{ background: 'rgba(132, 94, 247, 0.1)', borderRadius: 8, border: '1px solid rgba(132, 94, 247, 0.2)' }}>
                <Box style={{ position: 'relative', width: 60, height: 60 }}>
                  <img src={attachedImage} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                  <ActionIcon
                    size="xs"
                    color="red"
                    variant="filled"
                    style={{ position: 'absolute', top: -5, right: -5, borderRadius: '50%' }}
                    onClick={() => setAttachedImage(null)}
                  >
                    <IconX size={10} />
                  </ActionIcon>
                </Box>
                <Text size="xs" c="dimmed">Image attached</Text>
              </Group>
            )}

            <Flex gap="xs" align="flex-end">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />

              <Textarea
                placeholder="Message your agent..."
                flex={1}
                minRows={1}
                maxRows={5}
                autosize
                value={commandText}
                onChange={(e) => setCommandText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                styles={{
                  input: {
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'white',
                    borderRadius: 12,
                    fontSize: '14px',
                    '&:focus': {
                      borderColor: 'rgba(132, 94, 247, 0.5)',
                    }
                  }
                }}
              />

              <Stack gap={4}>
                {commandText || attachedImage ? (
                  <ActionIcon
                    size="lg"
                    radius="xl"
                    variant="gradient"
                    gradient={{ from: 'violet', to: 'indigo' }}
                    onClick={handleSend}
                  >
                    <IconSend size={18} />
                  </ActionIcon>
                ) : (
                  <ActionIcon
                    size="lg"
                    radius="xl"
                    variant={isListening ? 'filled' : 'light'}
                    color="violet"
                    onClick={isListening ? stopListening : startListening}
                  >
                    {isListening ? <IconPlayerStop size={18} /> : <IconMicrophone size={18} />}
                  </ActionIcon>
                )}
              </Stack>
            </Flex>
          </Stack>
        </Box>
      </Flex>

      <style>
        {`
          .pulse-dot {
            animation: pulse 1.5s infinite;
          }
          @keyframes pulse {
            0% { transform: scale(0.9); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.5; }
            100% { transform: scale(0.9); opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
}

export default App;
